import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchTabLockStatus } from '../../store/investor/action';
import type { ModuleId, ReportData } from './data/types';
import { MODULES, MODULE_ORDER, MODULE_META_EXTRA } from './modules/moduleRegistry';
import {
    hasUsableSubState,
    resolveSubOptions,
    seedSubOptions,
    specsFor,
    stateFor,
    subStateForTurnOn,
    toRailSubOptions,
    type RailSubOption,
    type SubOptionsByModule,
} from './subOptionState';
import { useAicaReportData, type FetchStatus, type UseAicaReportData } from './useAicaReportData';

export type ModuleRailItem = {
    id: ModuleId;
    title: string;
    category: string;
    description: string;
    dataSource: string;
    enabled: boolean;
    locked: boolean;
    lockedMessage?: string;
    hasData: boolean;
    subOptions?: RailSubOption[];
};

export type UseAicaReportV2Logic = {
    data: ReportData;
    status: FetchStatus;
    errors: UseAicaReportData['errors'];
    refetch: () => void;
    orderedModules: ModuleId[];
    enabledModules: ModuleId[];
    modulesPresent: ModuleId[];
    lockedModuleSet: Set<ModuleId>;
    railItems: ModuleRailItem[]; // filtered + sorted (enabled above unselected), for sidebar rendering
    searchTerm: string;
    setSearchTerm: (s: string) => void;
    toggleModule: (id: ModuleId) => void;
    subOptions: SubOptionsByModule;
    toggleSubOption: (id: ModuleId, subId: string) => void;
    selectAll: () => void;
    deselectAll: () => void;
    allEnabled: boolean;
    reorder: (id: ModuleId, targetId: ModuleId, position?: 'before' | 'after') => void;
    moveUp: (id: ModuleId) => void;
    moveDown: (id: ModuleId) => void;
    preparedFor: string;
    setPreparedFor: (s: string) => void;
    resetOrder: () => void;
};

export const useAicaReportV2Logic = (assesseeOrgId?: string): UseAicaReportV2Logic => {
    const { data, status, errors, refetch } = useAicaReportData(assesseeOrgId);
    const dispatch = useAppDispatch();
    const tabLockStatus = useAppSelector((state: any) => state.investor?.tabLockStatus) as
        | Record<string, { isLocked?: boolean; lockedMessage?: string }>
        | null
        | undefined;

    useEffect(() => {
        if (!assesseeOrgId) return;
        const tabIds = Array.from(new Set(MODULE_ORDER.map((id) => MODULE_META_EXTRA[id]?.lockTabId).filter(Boolean) as string[]));
        if (tabIds.length === 0) return;
        dispatch(fetchTabLockStatus({ tabsList: tabIds, assesseeOrgId }));
    }, [assesseeOrgId, dispatch]);

    const modulesPresent = useMemo<ModuleId[]>(
        () => MODULE_ORDER.filter((id) => MODULES[id as keyof typeof MODULES].hasData(data)),
        [data],
    );

    const lockedModuleSet = useMemo<Set<ModuleId>>(() => {
        const set = new Set<ModuleId>();
        const statusReady = !!tabLockStatus;
        MODULE_ORDER.forEach((id) => {
            const tabId = MODULE_META_EXTRA[id]?.lockTabId;
            if (!tabId) return;
            // Banking is data-driven, not subscription-gated in this report: if
            // the banking data is actually present, surface the module even when
            // the `bankingAndFinancials` tab reports locked (or lock status hasn't
            // resolved yet). Presence of data is the source of truth here — a
            // locked tab with available data should still render.
            if (id === 'bankingOverview' && MODULES.bankingOverview.hasData(data)) return;
            if (!statusReady || tabLockStatus[tabId]?.isLocked) set.add(id);
        });
        return set;
    }, [tabLockStatus, data]);

    const [orderedModules, setOrderedModules] = useState<ModuleId[]>(MODULE_ORDER);
    const [enabledModules, setEnabledModules] = useState<ModuleId[]>([]);
    const [rememberedSubOptions, setRememberedSubOptions] = useState<SubOptionsByModule>({});
    const [preparedFor, setPreparedFor] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    // Track whether the user has interacted with selection — once true, never auto-reset.
    const userTouchedRef = useRef(false);
    // Seed enabled set once data + lock state are ready, then leave it alone.
    const seededRef = useRef(false);

    useEffect(() => {
        if (userTouchedRef.current) return;
        if (modulesPresent.length === 0) return;
        // Wait for tabLockStatus to actually resolve before seeding so users don't see locked modules flash on.
        if (status !== 'ready') return;
        // Modules flagged `defaultDisabled` (legacy / archived) start unchecked — they still appear in the rail at
        // the bottom and can be toggled on, but aren't part of the default report.
        const seededSubOptions = seedSubOptions(data);
        const next = modulesPresent.filter(
            (id) =>
                !lockedModuleSet.has(id) && !MODULE_META_EXTRA[id]?.defaultDisabled && hasUsableSubState(id, data, seededSubOptions[id]),
        );
        setRememberedSubOptions(seededSubOptions);
        setEnabledModules(next);
        seededRef.current = true;
    }, [modulesPresent, lockedModuleSet, status, data]);

    // Drop any module from the enabled set if it becomes locked, loses data, or has no available sub-switch left on.
    useEffect(() => {
        if (!seededRef.current) return;
        setEnabledModules((prev) =>
            prev.filter(
                (id) => modulesPresent.includes(id) && !lockedModuleSet.has(id) && hasUsableSubState(id, data, rememberedSubOptions[id]),
            ),
        );
    }, [modulesPresent, lockedModuleSet, data, rememberedSubOptions]);

    const subOptions = useMemo(() => resolveSubOptions(data, rememberedSubOptions), [data, rememberedSubOptions]);

    const toggleModule = useCallback(
        (id: ModuleId) => {
            if (lockedModuleSet.has(id) || !modulesPresent.includes(id)) return;
            userTouchedRef.current = true;
            if (!enabledModules.includes(id)) {
                setRememberedSubOptions((prev) =>
                    specsFor(id, data).length ? { ...prev, [id]: subStateForTurnOn(id, data, prev[id]) } : prev,
                );
            }
            setEnabledModules((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
        },
        [lockedModuleSet, modulesPresent, enabledModules, data],
    );

    const toggleSubOption = useCallback(
        (id: ModuleId, subId: string) => {
            const specs = specsFor(id, data);
            if (!specs.find((s) => s.id === subId)?.isAvailable(data)) return;
            userTouchedRef.current = true;
            setRememberedSubOptions((prev) => {
                const current = stateFor(specs, data, prev[id]);
                return { ...prev, [id]: { ...current, [subId]: !current[subId] } };
            });
        },
        [data],
    );

    const selectAll = useCallback(() => {
        userTouchedRef.current = true;
        const selectable = modulesPresent.filter((id) => !lockedModuleSet.has(id));
        setRememberedSubOptions((prev) => ({
            ...prev,
            ...Object.fromEntries(
                selectable.filter((id) => specsFor(id, data).length).map((id) => [id, subStateForTurnOn(id, data, prev[id])]),
            ),
        }));
        setEnabledModules(selectable);
    }, [modulesPresent, lockedModuleSet, data]);

    const deselectAll = useCallback(() => {
        userTouchedRef.current = true;
        setEnabledModules([]);
    }, []);

    const allEnabled = useMemo(() => {
        const selectable = modulesPresent.filter((id) => !lockedModuleSet.has(id));
        return selectable.length > 0 && selectable.every((id) => enabledModules.includes(id));
    }, [modulesPresent, lockedModuleSet, enabledModules]);

    const move = useCallback((id: ModuleId, delta: -1 | 1) => {
        setOrderedModules((prev) => {
            const idx = prev.indexOf(id);
            if (idx < 0) return prev;
            const next = idx + delta;
            if (next < 0 || next >= prev.length) return prev;
            const out = [...prev];
            const [item] = out.splice(idx, 1);
            out.splice(next, 0, item);
            return out;
        });
    }, []);

    const moveUp = useCallback((id: ModuleId) => move(id, -1), [move]);
    const moveDown = useCallback((id: ModuleId) => move(id, 1), [move]);

    const reorder = useCallback((id: ModuleId, targetId: ModuleId, position: 'before' | 'after' = 'before') => {
        if (id === targetId) return;
        setOrderedModules((prev) => {
            const from = prev.indexOf(id);
            const toRaw = prev.indexOf(targetId);
            if (from < 0 || toRaw < 0) return prev;
            const out = [...prev];
            const [item] = out.splice(from, 1);
            // Recompute target index after removal of source.
            let target = out.indexOf(targetId);
            if (position === 'after') target += 1;
            out.splice(target, 0, item);
            return out;
        });
    }, []);

    const resetOrder = useCallback(() => {
        userTouchedRef.current = true;
        setOrderedModules(MODULE_ORDER);
        setEnabledModules(modulesPresent.filter((id) => !lockedModuleSet.has(id)));
    }, [modulesPresent, lockedModuleSet]);

    const enabledInOrder = useMemo<ModuleId[]>(
        () => orderedModules.filter((id) => enabledModules.includes(id) && !lockedModuleSet.has(id)),
        [orderedModules, enabledModules, lockedModuleSet],
    );

    const railItems = useMemo<ModuleRailItem[]>(() => {
        const term = searchTerm.trim().toLowerCase();

        const enabledSet = new Set(enabledModules);

        const allItems: ModuleRailItem[] = orderedModules.map((id) => {
            const mod = MODULES[id as keyof typeof MODULES];
            const extra = MODULE_META_EXTRA[id];
            const hasData = mod.hasData(data);
            const locked = lockedModuleSet.has(id);
            return {
                id,
                title: extra?.railTitle ?? mod.meta.title,
                category: extra?.category ?? 'General',
                description: mod.meta.description,
                dataSource: mod.meta.dataSource,
                enabled: enabledSet.has(id) && !locked,
                locked,
                lockedMessage: locked ? tabLockStatus?.[extra?.lockTabId ?? '']?.lockedMessage : undefined,
                hasData,
                subOptions: toRailSubOptions(id, data, subOptions[id]),
            };
        });

        const filtered = term
            ? allItems.filter((x) => x.title.toLowerCase().includes(term) || x.category.toLowerCase().includes(term))
            : allItems;

        // Group order: Modules Available (Enabled → Disabled) above Module Unavailable (Locked → No Data).
        // Preserve the user's drag-reorder within each bucket.
        const enabled: ModuleRailItem[] = [];
        const disabled: ModuleRailItem[] = [];
        const locked: ModuleRailItem[] = [];
        const noData: ModuleRailItem[] = [];
        filtered.forEach((it) => {
            if (it.enabled) enabled.push(it);
            else if (it.locked) locked.push(it);
            else if (!it.hasData) noData.push(it);
            else disabled.push(it);
        });
        return [...enabled, ...disabled, ...locked, ...noData];
    }, [orderedModules, enabledModules, lockedModuleSet, tabLockStatus, searchTerm, data, subOptions]);

    return {
        data,
        status,
        errors,
        refetch,
        orderedModules,
        enabledModules: enabledInOrder,
        modulesPresent,
        lockedModuleSet,
        railItems,
        searchTerm,
        setSearchTerm,
        toggleModule,
        subOptions,
        toggleSubOption,
        selectAll,
        deselectAll,
        allEnabled,
        reorder,
        moveUp,
        moveDown,
        preparedFor,
        setPreparedFor,
        resetOrder,
    };
};
